import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default:
                    "bg-primary rounded-full text-primary-foreground hover:bg-primary/90",
                destructive:
                    "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
                outline:
                    "text-neutral-dark border border-border-primary rounded-full bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
                secondary:
                    "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
                ghost: "rounded-full backdrop-blur-md hover:bg-icon-base hover:text-accent-foreground",
                ghostWhite:
                    "bg-white/30 rounded-full backdrop-blur-md hover:bg-white/50",
                link: "text-primary underline-offset-1 hover:underline",
                purpleGradient:
                    "to-surface-gradient-ligth-purple rounded-full bg-gradient-to-b from-purple-800 font-body text-white",
                lavenderGradient:
                    "rounded-full bg-gradient-to-b from-white/80 to-[#A47AD9] font-body text-assistant-ink",
                purpleGradientWithoutBorder:
                    "transition-all duration-300  bg-gradient-to-bl from-[#925aff] to-[#43006c] outline outline-2 outline-offset-[-2px] outline-white/10 font-body text-sm font-semibold text-white hover:brightness-125",
                circleFloating:
                    "fixed bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-primary-base shadow-lg hover:bg-gray-600 transition-all border border-white/30",
                menuItem:
                    "rounded-md hover:bg-surface-secondary hover:text-accent-foreground w-full justify-start",
                combobox:
                    "rounded-md border border-border-primary text-foreground hover:bg-surface-primary hover:text-accent-foreground [&_div]:w-full text-left",
                action: "rounded-full bg-surface-primaryHover hover:bg-surface-primaryHover/70 transition-all duration-300",
                invisible:
                    "bg-transparent transition-all duration-300 whitespace-normal",
                deepFocus:
                    "bg-surface-dark-deep-focus border border-white/10 text-surface-text-deep-focus hover:bg-surface-dark-deep-focus/70 transition-all duration-300",
                deepFocusEntry:
                    "bg-white/20 text-text-subtitle backdrop-blur-xl font-body shadow-[0_2px_6px_rgba(15,23,42,0.25),0_1px_2px_rgba(15,23,42,0.04)] transition-[background-color,box-shadow] duration-300 ease-out hover:bg-white/40 hover:shadow-[0_2px_16px_rgba(15,23,42,0.12),0_1px_3px_rgba(15,23,42,0.30)] focus-visible:bg-white/40 focus-visible:shadow-[0_2px_8px_rgba(15,23,42,0.12),0_1px_3px_rgba(15,23,42,0.06)]",
                paragraph: "h-auto whitespace-normal text-left [&_div]:w-fit",
                social: " rounded-full border border-neutral-lighter bg-surface-primary text-text-active shadow-sm transition-all hover:bg-neutral-lighter font-semibold justify-start",
            },
            gradientBorderVariant: {
                purpleGradient:
                    "max-w-full transition-all duration-300 to-surface-gradient-ligth-purple bg-gradient-to-l from-purple-700 h-full rounded-full px-6 hover:brightness-125",
                lavenderGradient:
                    "max-w-full transition-all duration-300 to-[#D6C1F3] bg-gradient-to-l from-[#B48CE6] h-full rounded-full px-6 hover:brightness-105",
            },
            size: {
                default: "h-9 px-4 py-2",
                xs: "h-6 px-2 py-1",
                sm: "h-8 rounded-md px-3 text-xs",
                lg: "rounded-full h-10 px-4 py-2.5",
                xl: "rounded-full h-12 px-6 py-3 text-base",
                icon: "h-11 w-11",
                gradient: "h-10 p-[2px]",
                smIcon: "h-7 w-7",
                tinyCircle: "rounded-full h-5 w-5",
                smallCircle: "rounded-full h-8 w-8",
                circle: "rounded-full h-10 w-10",
                blank: "",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

export interface ButtonProps
    extends
        React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    icon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            gradientBorderVariant,
            icon,
            asChild = false,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : "button";
        const buttonContent = (
            <div className="flex w-max items-center gap-1.5">
                {icon}
                {props.children}
            </div>
        );

        return (
            <Comp
                ref={ref}
                className={cn(
                    buttonVariants({
                        variant,
                        size,
                        className,
                    }),
                )}
                {...props}
            >
                {gradientBorderVariant ? (
                    <div
                        className={cn(
                            buttonVariants({ gradientBorderVariant }),
                        )}
                    >
                        {buttonContent}
                    </div>
                ) : (
                    buttonContent
                )}
            </Comp>
        );
    },
);
Button.displayName = "Button";

export { Button, buttonVariants };
